import { useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';

const schema = yup.object({
  date: yup.string().required('Оберіть дату'),
  topics: yup.string().required('Теми обовʼязкові'),
  taskDescription: yup.string().nullable(),
  notes: yup.string().nullable(),
  theoryScore: yup.number().min(1).max(10).required('1-10'),
  practiceScore: yup.number().min(1).max(10).required('1-10'),
  type: yup.string().nullable(),
  duration: yup.string().nullable(),
});

const typeOptions = [
  { value: 'ONLINE_TEST', label: 'Онлайн-тест' },
  { value: 'PRACTICAL', label: 'Практичні завдання' },
  { value: 'ORAL', label: 'Усне опитування' },
  { value: 'OTHER', label: 'Інше' },
];

const AssessmentFormDialog = ({ open, onClose, initialValues, onSubmit, title }) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (open) reset(initialValues);
  }, [initialValues, open, reset]);

  const handleFormSubmit = handleSubmit(async (values) => {
    const payload = {
      ...values,
      theoryScore: Number(values.theoryScore),
      practiceScore: Number(values.practiceScore),
      finalScore: Math.round((Number(values.theoryScore) + Number(values.practiceScore)) / 2),
    };
    await onSubmit(payload);
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, color: '#1E2A4A' }}>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} mt={1}>
          <Controller
            name="date"
            control={control}
            render={({ field }) => (
              <DatePicker
                label="Дата"
                format="DD.MM.YYYY"
                value={field.value ? dayjs(field.value) : null}
                onChange={(val) => field.onChange(val ? val.format('YYYY-MM-DD') : '')}
                slotProps={{ textField: { fullWidth: true, error: Boolean(errors.date), helperText: errors.date?.message } }}
              />
            )}
          />
          <TextField
            label="Теми контролю"
            {...register('topics')}
            error={Boolean(errors.topics)}
            helperText={errors.topics?.message}
            fullWidth
          />
          <TextField
            label="Зміст завдань"
            multiline
            minRows={3}
            {...register('taskDescription')}
            fullWidth
          />
          <TextField
            label="Примітки"
            multiline
            minRows={2}
            {...register('notes')}
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Теорія (1-10)"
              type="number"
              inputProps={{ min: 1, max: 10 }}
              {...register('theoryScore')}
              error={Boolean(errors.theoryScore)}
              helperText={errors.theoryScore?.message}
              fullWidth
            />
            <TextField
              label="Практика (1-10)"
              type="number"
              inputProps={{ min: 1, max: 10 }}
              {...register('practiceScore')}
              error={Boolean(errors.practiceScore)}
              helperText={errors.practiceScore?.message}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Тип"
              select
              {...register('type')}
              fullWidth
              defaultValue={initialValues?.type || ''}
            >
              <MenuItem value="">—</MenuItem>
              {typeOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Тривалість"
              placeholder="45 хв"
              {...register('duration')}
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} sx={{ color: '#8891A5' }}>Скасувати</Button>
        <Button
          variant="contained"
          onClick={handleFormSubmit}
          disabled={isSubmitting}
          sx={{ bgcolor: '#4357AD', '&:hover': { bgcolor: '#3648A0' }, borderRadius: 2, px: 3 }}
        >
          Зберегти
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssessmentFormDialog;
